import React from 'react';
import useCurrentTextStyle from '@src/hooks/useCurrentTextStyle';

export const ArticleDate = ({ article, settings, }) => {
  const { getCurrentTextStyle } = useCurrentTextStyle();

  const {
    colour_scheme,
    classes,
    date_format = '%B %d, %Y',
    date_style,
    date_style_mobile,
    date_style_tablet,
    date_style_ultrawide,
    date_style_enable_responsive,
    date_alignment = 'left',
    padding_top = 0,
    padding_right = 0,
    padding_bottom = 0,
    padding_left = 0,
    margin_top = 0,
    margin_right = 0,
    margin_bottom = 0,
    margin_left = 0
  } = settings;
  if (!article?.published_at) return null;

  const formatDate = (dateString: string, format: string): string => {
    // Handle different date formats that might come from Shopify
    let date: Date;
    
    // Try parsing as-is first
    date = new Date(dateString);
    
    // If that fails, try parsing as a Shopify date format
    if (isNaN(date.getTime())) {
      // Shopify sometimes returns dates in format like "2024-01-15T10:30:00-05:00"
      // or other ISO-like formats that might need cleaning
      const cleanedDateString = dateString.replace(/[^\d\-T:\.Z\+]/g, '');
      date = new Date(cleanedDateString);
    }
    
    // If still invalid, try extracting just the date part
    if (isNaN(date.getTime())) {
      const dateMatch = dateString.match(/(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        date = new Date(dateMatch[1] + 'T00:00:00Z');
      }
    }
    
    // Handle invalid dates - return original string as fallback
    if (isNaN(date.getTime())) {
      console.warn('Invalid date format:', dateString);
      return dateString;
    }
    
    // Handle special cases first
    if (format === '%d/%m/%Y') {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }

    if (format === '%Y-%m-%d') {
      return date.toISOString().split('T')[0];
    }

    if (format === '%d %B %Y') {
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'long' });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    }

    if (format === '%d %b %Y') {
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    }
    
    // Map Liquid format strings to JavaScript formatting
    const formatMap: { [key: string]: Intl.DateTimeFormatOptions } = {
      '%B %d, %Y': { month: 'long', day: 'numeric', year: 'numeric' },      // January 15, 2024
      '%b %d, %Y': { month: 'short', day: 'numeric', year: 'numeric' },     // Jan 15, 2024
      '%m/%d/%Y': { month: '2-digit', day: '2-digit', year: 'numeric' },    // 01/15/2024
      '%B %Y': { month: 'long', year: 'numeric' },                          // January 2024
      '%b %Y': { month: 'short', year: 'numeric' }                          // Jan 2024
    };

    // Use Intl.DateTimeFormat for standard formats
    const options = formatMap[format];
    if (options) {
      return new Intl.DateTimeFormat('en-US', options).format(date);
    }

    // Fallback to default format
    return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(date);
  };

  const formattedDate = formatDate(article.published_at, date_format);
  
  // Create ISO date safely for the datetime attribute
  const createIsoDate = (dateString: string): string => {
    let date = new Date(dateString);
    
    // Try parsing with cleanup if initial parsing fails
    if (isNaN(date.getTime())) {
      const cleanedDateString = dateString.replace(/[^\d\-T:\.Z\+]/g, '');
      date = new Date(cleanedDateString);
    }
    
    // Extract date part if still invalid
    if (isNaN(date.getTime())) {
      const dateMatch = dateString.match(/(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        return dateMatch[1]; // Return just the YYYY-MM-DD part
      }
    }
    
    // Return ISO date or fallback
    return isNaN(date.getTime()) ? dateString : date.toISOString().split('T')[0];
  };
  
  const isoDate = createIsoDate(article.published_at);

  return (
    <div 
      className={`
        w-full
        color-${colour_scheme}
        text-${date_alignment}
        pt-[${padding_top}px]
        pb-[${padding_bottom}px]
        pl-[${padding_left}px]
        pr-[${padding_right}px]
        mt-[${margin_top}px]
        mb-[${margin_bottom}px]
        ml-[${margin_left}px]
        mr-[${margin_right}px]
        ${classes}
      `}      >
        <time 
          dateTime={isoDate}
          className={getCurrentTextStyle(date_style, date_style_enable_responsive, date_style_mobile, date_style_tablet, date_style_ultrawide) || 'b1'}
        >
          {formattedDate}
        </time>
      </div>
  );
};